/**
 * WebSocket client for global chat at /ws/chat.
 */

import { getWebSocketUrl } from '../utils/wsUrl';

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

const getChatSocketUrl = () => {
  if (process.env.REACT_APP_CHAT_WS_URL) {
    return process.env.REACT_APP_CHAT_WS_URL;
  }
  return getWebSocketUrl('/ws/chat');
};

export function createChatSocket() {
  const listeners = {};
  let ws = null;
  let shouldReconnect = false;
  let socketGeneration = 0;
  let reconnectDelay = RECONNECT_BASE_MS;
  let reconnectTimer = null;
  const pending = new Map();
  let reqCounter = 0;

  const emit = (event, data) => {
    (listeners[event] || []).forEach((fn) => {
      try {
        fn(data);
      } catch (_) {}
    });
  };

  const on = (event, fn) => {
    if (!listeners[event]) listeners[event] = [];
    listeners[event].push(fn);
    return () => off(event, fn);
  };

  const off = (event, fn) => {
    if (!listeners[event]) return;
    listeners[event] = listeners[event].filter((f) => f !== fn);
  };

  const parsePayload = (payload) => {
    if (typeof payload !== 'string') {
      return payload;
    }
    try {
      return JSON.parse(payload);
    } catch {
      return payload;
    }
  };

  const rejectPending = (message) => {
    pending.forEach(({ reject }) => reject(new Error(message)));
    pending.clear();
  };

  const handleMessage = (raw) => {
    let env;
    try {
      env = JSON.parse(raw.data);
    } catch {
      return;
    }

    const { type, reqId, payload } = env;
    const parsed = parsePayload(payload);

    if (reqId && pending.has(reqId)) {
      const { resolve, reject, expectType } = pending.get(reqId);
      pending.delete(reqId);
      if (type === 'error') {
        const err = new Error(parsed?.message || 'Chat socket error');
        err.code = parsed?.code;
        err.retryAfterSec = parsed?.retryAfterSec;
        reject(err);
        return;
      }
      if (expectType && type !== expectType) {
        resolve({ type, payload: parsed });
        return;
      }
      resolve(parsed);
      return;
    }

    if (type === 'error') {
      emit('error', parsed);
      return;
    }

    emit(type, parsed);
  };

  const connect = () => {
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const myGen = ++socketGeneration;
    shouldReconnect = true;
    const socket = new WebSocket(getChatSocketUrl());
    ws = socket;

    socket.onopen = () => {
      if (myGen !== socketGeneration) {
        socket.close();
        return;
      }
      reconnectDelay = RECONNECT_BASE_MS;
      emit('connected');
    };

    socket.onmessage = handleMessage;

    socket.onclose = () => {
      if (ws === socket) {
        ws = null;
      }
      emit('disconnected');
      rejectPending('connection closed');
      if (shouldReconnect && myGen === socketGeneration) {
        clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(() => {
          reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
          connect();
        }, reconnectDelay);
      }
    };

    socket.onerror = () => {
      emit('error', { message: 'WebSocket bağlantı hatası' });
    };
  };

  const send = (type, payload = {}, expectType = null) =>
    new Promise((resolve, reject) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket bağlı değil'));
        return;
      }
      const reqId = `${Date.now()}-${++reqCounter}`;
      pending.set(reqId, { resolve, reject, expectType });
      ws.send(JSON.stringify({ type, reqId, payload }));
    });

  const close = () => {
    socketGeneration += 1;
    shouldReconnect = false;
    clearTimeout(reconnectTimer);
    rejectPending('connection closed');
    if (!ws) return;
    const socket = ws;
    ws = null;
    if (socket.readyState === WebSocket.OPEN) {
      socket.close();
    }
    // CONNECTING: close() çağırma; socket açılınca disposed olduğu için kendini kapatır.
  };

  return { connect, send, close, on, off, emit };
}

export default createChatSocket;
